import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useTravelStore as useTravelHiringStore } from '@/modules/travel/hiring/stores/index'
import { useTravelStore as useTravelRaterStore } from '@/modules/travel/rater/stores/index'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { raterGuard } from './guards'
import type { RouteLocationNormalized, NavigationGuardNext, RouteRecordRaw } from 'vue-router'
import SpanishBranches from '@/modules/core/models/SpanishBranches'
import ModifyInsuranceModal from '@/modules/travel/rater/views/modals/ModifyInsuranceModal.vue'
import Step0Form from '@/modules/travel/hiring/views/forms/Step0Form.vue'
import BranchEnum from '@/modules/core/models/BranchEnum'

const RATER_TITLE = 'Precios y Tarifas de los Seguros de Viajes | ASISA'
const RATER_DESCRIPTION =
  'Obtén el precio de tu seguro de viaje ASISA en pocos segundos. Compara tarifas y elige la cobertura que mejor se adapte a tus planes.'

export const TRAVEL_RATER_ROUTES = [
  {
    path: SpanishBranches.travel,
    name: 'rater-travel',
    component: () => import('../modules/common/rater/views/BaseView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.TRAVEL,
      defaultBusinessLineId: 'AV01'
    },
    props: () => ({
      store: useTravelRaterStore()
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing-almost-got-it')
      await raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.travel}/:productCode/:clientCode`,
    name: 'travel-product',
    component: () => import('../modules/common/rater/views/ProductBaseView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.TRAVEL
    },
    props: () => ({
      store: useTravelRaterStore(),
      modifyModal: ModifyInsuranceModal
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.travel}/propuestas`,
    name: 'travel-insurance-proposals',
    component: () => import('@/modules/travel/rater/views/BaseProductsView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.TRAVEL
    },
    props: () => ({
      store: useTravelRaterStore(),
      modifyModal: ModifyInsuranceModal
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.travel}/:id/ficha/:billingCycle`,
    name: 'travel-product-view',
    component: () => import('@/modules/travel/rater/views/ProductView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.TRAVEL
    },
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  }
] as RouteRecordRaw[]

export const TRAVEL_HIRING_ROUTES = [
  {
    path: `${SpanishBranches.travel}/:id`,
    name: 'hiring-travel',
    component: () => import('@/modules/common/hiring/views/BaseView.vue'),
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.TRAVEL,
      defaultBusinessLineId: 'AV01'
    },
    props: () => ({
      store: useTravelHiringStore(),
      repricingForm: Step0Form
    }),
    beforeEnter: (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      const message = useCommonStore().loaderMessage ?? 'common.loading.starting-hiring'
      useLoadingStore().startLoading('common.loading.processing', message)
      raterGuard(to, from, next)
      useCommonStore().loaderMessage = null
    }
  },
  {
    path: `${SpanishBranches.travel}/:id/retorno-pago`,
    name: 'hiring-travel-payment-return',
    meta: {
      title: 'Seguros Médicos ASISA'
    },
    component: () => import('../modules/common/hiring/views/SignatureCompleteView.vue'),
    props: {
      hash: false,
      branchName: BranchEnum.TRAVEL
    },
    beforeEnter: () => {
      const loadingStore = useLoadingStore()
      loadingStore.stopLoading()
      loadingStore.startLoading(
        'common.loading.processing',
        'common.loading.processing-almost-got-it'
      )
    }
  },
  {
    path: `${SpanishBranches.travel}/firma-completada/:hash`,
    name: 'travel-hiring-signature-done',
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.TRAVEL
    },
    beforeEnter: () => {
      const loadingStore = useLoadingStore()
      loadingStore.stopLoading()
      loadingStore.startLoading(
        'common.loading.processing',
        'common.loading.processing-almost-got-it'
      )
    },
    component: () => import('../modules/common/hiring/views/SignatureCompleteView.vue'),
    props: {
      branchName: BranchEnum.TRAVEL
    }
  }
] as RouteRecordRaw[]
